import { hellopanda_func as $ } from '../../../assets/script/func.js';

/**
 * HELLOPANDA_YOUTUBE
 * - observe 클래스가 있을 때만 visibility 체크 (play/pause)
 * - enablejsapi=1은 블록에서 직접 처리
 * - YouTube IFrame API는 Promise로 안전 로드
 * - IntersectionObserver threshold: 0 (1px만 보여도 play)
 * - 브라우저 탭 비활성화 시 자동 pause/play
 */

const HELLOPANDA_YOUTUBE = {

  resizeSet: new Set(),
  resizeWidth: window.innerWidth,
  resizeHeight: window.innerHeight,
  resizeHeightAllow: $.resizeHeightAllow,

  // Map<iframeId, { player: YT.Player, observer: IntersectionObserver, isIntersecting: boolean, iframe }>
  instances: new Map(),

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._initCore());
    } else {
      this._initCore();
    }
  },

  async _initCore() {
    // 비율 관련 처리
    this.youtube_ratio();

    // observe 옵션이 붙은 유튜브 iframe만 선택
    const selector =
      '.hellopanda-blocks-youtube.yt-mobile-expand.hellopanda-blocks-youtube-observe .hellopanda-blocks-youtube-player iframe';
    const targetIframes = Array.from(document.querySelectorAll(selector));

    if (!targetIframes.length) return;

    // --- YouTube API 로드 (Promise) ---
    try {
      await this.loadYouTubeAPI();
    } catch (e) {
      console.error('HELLOPANDA: YouTube API 로드 실패', e);
      return;
    }

    // --- 플레이어 생성 및 observe 등록 ---
    this.setupPlayers(targetIframes);

    // --- 브라우저 탭 visibilitychange 이벤트 등록 ---
    document.addEventListener('visibilitychange', () => {
      this.instances.forEach(({ player, isIntersecting }) => {
        try {
          if (!document.hidden && isIntersecting) {
            player.playVideo?.();
          } else {
            player.pauseVideo?.();
          }
        } catch (e) {
          console.warn('HELLOPANDA: visibilitychange play/pause 실패', e);
        }
      });
    });
  },

  // Promise 기반 YouTube IFrame API 로더
  loadYouTubeAPI() {
    return new Promise((resolve, reject) => {
      if (window.YT && window.YT.Player) {
        resolve();
        return;
      }

      const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
      if (!existing) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.async = true;
        tag.onerror = () => reject(new Error('YouTube iframe_api 로드 실패'));
        document.head.appendChild(tag);
      }

      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prev === 'function') {
          try { prev(); } catch (e) {}
        }
        resolve();
      };

      setTimeout(() => {
        if (!(window.YT && window.YT.Player)) {
          reject(new Error('YouTube iframe_api 응답 없음 (timeout)'));
        }
      }, 10000);
    });
  },

  setupPlayers(iframes) {
    if (!window.YT || !window.YT.Player) {
      console.error('HELLOPANDA: YT.Player 사용할 수 없음');
      return;
    }

    iframes.forEach((iframe) => {
      const id = iframe.id || `hp-yt-${Math.random().toString(36).slice(2, 9)}`;
      iframe.id = id;

      if (this.instances.has(id)) return;

      try {
        const player = new YT.Player(id, {
          events: {
            onReady: () => this.observeVisibility(id, player, iframe),
            onError: (err) => console.warn('HELLOPANDA: player error', id, err),
          },
        });

        this.instances.set(id, { player, observer: null, iframe, isIntersecting: false });
      } catch (err) {
        console.error('HELLOPANDA: YT.Player 생성 실패', err);
      }
    });
  },

  observeVisibility(id, player, iframe) {
    const existing = this.instances.get(id);
    if (existing && existing.observer) return;

    let isIntersecting = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isIntersecting = entry.isIntersecting;

          try {
            if (isIntersecting) {
              player.playVideo?.();
            } else {
              player.pauseVideo?.();
            }
          } catch (e) {
            console.warn('HELLOPANDA: play/pause 시도 중 오류', e);
          }
        });

        // 상태 저장
        this.instances.set(id, { player, observer, iframe, isIntersecting });
      },
      { threshold: 0 } // 1px이라도 보이면 true
    );

    observer.observe(iframe);
    this.instances.set(id, { player, observer, iframe, isIntersecting });
  },

  // 딜레이
  debounce: function(fn, delay) {

    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };

  },

  // 진짜 resize 발생 여부 확인
  checkResize() {
    const _ = this;
    const w = window.innerWidth;
    const h = window.innerHeight;
    let changed = false;

    if (w !== _.resizeWidth) {
      _.resizeWidth = w;
      changed = true;
    } else if (Math.abs(h - _.resizeHeight) >= _.resizeHeightAllow) {
      _.resizeHeight = h;
      changed = true;
    }

    return changed;
  },

  // 모든 구독자에게 알림
  resizeNotify: function() {
    const _ = this;
    _.resizeSet.forEach(fn => fn(_.resizeWidth, _.resizeHeight));
  },

  // 모듈이 리사이즈 감지에 참여할 때
  resizeSubscribe: function(fn) {
    const _ = this;
    _.resizeSet.add(fn);
    return () => _.resizeSet.delete(fn);
  },

  // -----------------------------------------
  // youtube_ratio 함수 (리사이즈 핸들링 포함)
  // -----------------------------------------
  youtube_ratio() {

    const _ = this;

    function set_width(element) {
      let ratio = 16 / 9;
      const brandHeight = 120;
      const ratios = [
        { key: 'yt-ratio-4-3', value: 4 / 3 },
        { key: 'yt-ratio-1-1', value: 1 / 1 },
        { key: 'yt-ratio-21-9', value: 21 / 9 },
        { key: 'yt-ratio-9-16', value: 9 / 16 },
        { key: 'yt-ratio-3-2', value: 3 / 2 },
      ];

      const container = element.closest('.swiper') || element.closest('.hellopanda-blocks-youtube');
      if (!container) return;

      ratios.forEach((item) => {
        if (container.classList.contains(item.key)) {
          ratio = item.value;
        }
      });

      const minwidth = (container.offsetHeight + brandHeight) * ratio;
      element.style.minWidth = `${minwidth}px`;
    }

    const selector = '.hellopanda-blocks-youtube.yt-mobile-expand .hellopanda-blocks-youtube-player';
    const youtubes = Array.from(document.querySelectorAll(selector));
    if (!youtubes.length) return;

    // const handleResize = debounce(() => {
    //   youtubes.forEach((element) => set_width(element));
    // }, 200);
    /// youtubes.forEach((element) => set_width(element));

    function youtube_set_width() {
      youtubes.forEach((element) => set_width(element));
    }

    window.addEventListener('load', youtube_set_width());
    youtube_set_width();
    
    // window.addEventListener('resize', handleResize);

    // 구독형 리사이즈 ( IOS 개선 )
    const handleResize = _.debounce(() => {
      if (_.checkResize()) {
        _.resizeNotify();
      }
    }, 200);
    window.addEventListener('resize', handleResize.bind(this));

    // 구독으로 리사이즈 실행
    _.resizeSubscribe(() => {
      youtube_set_width();
    });

  },

  destroyAll() {
    this.instances.forEach(({ player, observer }) => {
      try { observer && observer.disconnect(); } catch (e) {}
      try { player && player.destroy?.(); } catch (e) {}
    });
    this.instances.clear();
  },
};

HELLOPANDA_YOUTUBE.init();